import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api } from '@/api/client';
import { recentlyActive } from '@/lib/activity';
import { useAuth } from '@/lib/useAuth';
import { RealtimeClient, type ChannelLike, type RealtimeDeps, type SocketLike } from './client';
import { RealtimeContext, type RealtimeContextValue } from './context';
import type { ClientFrame, RealtimeEnvelope, RealtimeStatus } from './protocol';

/** The browser's implementations of everything the client needs. */
function browserDeps(): RealtimeDeps {
  const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return {
    fetchTicket: async () =>
      (await api.post<{ ticket: string; expires_at: string }>('/realtime/tickets')).ticket,
    openSocket: (url) => new WebSocket(url) as unknown as SocketLike,
    socketUrl: `${scheme}://${window.location.host}/api/v1/realtime`,
    openChannel:
      typeof BroadcastChannel === 'undefined'
        ? null
        : (name) => new BroadcastChannel(name) as unknown as ChannelLike,
    requestLeadership:
      typeof navigator !== 'undefined' && 'locks' in navigator
        ? (name, holder) => {
            void navigator.locks.request(name, holder);
          }
        : null,
    setTimer: (fn, ms) => window.setTimeout(fn, ms),
    clearTimer: (handle) => window.clearTimeout(handle as number),
    isActive: () => recentlyActive(),
  };
}

/**
 * Starts the live connection while somebody is signed in, and stops it when
 * they sign out (ADR 0005). Everything inside can listen for events with
 * useRealtimeEvent and read the connection's state with useRealtimeStatus.
 */
export function RealtimeProvider({
  children,
  deps,
}: {
  children: ReactNode;
  /** Injected in tests; the browser's own APIs otherwise. */
  deps?: RealtimeDeps;
}) {
  const { identity } = useAuth();
  const clientRef = useRef<RealtimeClient | null>(null);
  if (clientRef.current === null) {
    clientRef.current = new RealtimeClient(deps ?? browserDeps());
  }
  const client = clientRef.current;

  const [status, setStatus] = useState<RealtimeStatus>(client.getStatus());
  useEffect(() => client.onStatus(setStatus), [client]);

  // Keyed on the user, not the identity object: /me is reloaded on every token
  // refresh, and reconnecting every fifteen minutes would be pointless churn.
  const userId = identity?.user_id ?? null;
  useEffect(() => {
    if (!userId) return;
    client.start();
    return () => client.stop();
  }, [client, userId]);

  const subscribe = useCallback(
    (fn: (env: RealtimeEnvelope) => void) => client.subscribe(fn),
    [client],
  );

  const send = useCallback((frame: ClientFrame) => client.send(frame), [client]);

  const value = useMemo<RealtimeContextValue>(() => ({ status, subscribe, send }), [status, subscribe, send]);
  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}
